import { memo, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { CustomerDTO } from '@six-pockets/shared';
import { UsersIcon } from '@/components/icons';
import { CustomerSuggestList } from '@/features/customers/components/CustomerSuggestList';
import { useCustomerSuggest } from '@/features/customers/useCustomerSuggest';
import { cx } from '@/utils/cx';
import styles from '@/styles/components/NameInput/style.scss';

interface NameInputProps {
  value: string;
  /** The name is an existing customer's (picked from the list), not typed text. */
  linked: boolean;
  /** A typed name. */
  onCommit: (name: string) => void;
  /** An existing customer picked from the list. */
  onPick: (customer: CustomerDTO) => void;
  label: string;
  className?: string;
  /** Focus the field when it appears (the second name, just added with ＋). */
  autoFocus?: boolean;
  placeholder?: string;
  /** A small button inside the box at its right end, before the customer mark (the row's ＋ for a second name). */
  action?: ReactNode;
}

/**
 * Inline-editable customer name. Typing offers the existing customers that match; picking one gives the booking that
 * customer. Otherwise it is a plain name: saved on blur or Enter; Escape reverts. Memoised (typing re-renders only
 * this).
 */
export const NameInput = memo(function NameInput({ value, linked, onCommit, onPick, label, className, autoFocus, placeholder = 'Add name', action }: NameInputProps) {
  const [draft, setDraft] = useState(value);
  /** The name is being typed in: the list is offered from the first keystroke until the field is left. */
  const [typing, setTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  /** A customer was just picked, or Escape pressed: the blur that follows must not save the typed text. */
  const skipCommit = useRef(false);

  useEffect(() => setDraft(value), [value]);

  const pick = (customer: CustomerDTO) => {
    skipCommit.current = true;
    setTyping(false);
    setDraft(customer.name);
    onPick(customer);
    inputRef.current?.blur();
  };

  // Searched only once something is typed, not on every focus. Whatever is typed is searched, also when it is the
  // name the row already has (a typed name can be turned into the customer by typing it again).
  const suggest = useCustomerSuggest(draft, typing, pick);

  const commit = () => {
    setTyping(false);
    if (skipCommit.current) {
      skipCommit.current = false;
      return;
    }
    const next = draft.trim();
    if (next !== value) onCommit(next);
  };

  return (
    <div className={cx(styles.root, className)}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={suggest.open}
        aria-controls={suggest.open ? listId : undefined}
        autoComplete="off"
        value={draft}
        placeholder={placeholder}
        autoFocus={autoFocus}
        aria-label={label}
        maxLength={80}
        className={cx(styles.input, !draft && styles.empty, linked && styles.linked, action != null && styles.withAction)}
        onChange={(e) => setDraft(e.target.value)}
        // Not in onChange: React skips that when the text ends up the same (the name selected and typed again).
        onInput={() => setTyping(true)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (suggest.onKeyDown(e)) return;
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            skipCommit.current = true;
            setDraft(value);
            e.currentTarget.blur();
          }
        }}
      />
      {action != null && <span className={cx(styles.action, linked && styles.actionBeforeMark)}>{action}</span>}
      {linked && (
        <span className={styles.mark} title="Existing customer">
          <UsersIcon size={13} />
        </span>
      )}
      {suggest.open && (
        <CustomerSuggestList anchorRef={inputRef} id={listId} results={suggest.results} active={suggest.active} onPick={pick} onClose={suggest.close} />
      )}
    </div>
  );
});
